import {
  ACTIONS,
  actionToBackendPermissionMap,
  ActionToPermissionType,
  BACKEND_PERMISSIONS,
  dataFilterToPermissionMap,
  navActionToPermissionMap
} from './constants'
import { IPermission } from '../../features/configureRoles/constants'
import {
  URL_ACCESS_CONTROL,
  FILTER_BY,
  IS_ALL,
  IS_UNRESOLVED,
  ROLE_SCOPES,
  URL_ALERTS,
  URL_ATTRIBUTES,
  URL_CONSENT_MANAGEMENT,
  URL_COOKIES_CONSENT,
  URL_DATA_SOURCES,
  URL_DEFINED_PROCESSES,
  URL_DOCUMENTS_CLASSIFIED,
  URL_DSR,
  URL_DSR_REQUEST_MANAGEMENT,
  URL_ENTITIES,
  URL_FILES,
  URL_AUTH_PROVIDERS,
  URL_INSIGHTS,
  URL_LABEL_MANAGEMENT,
  URL_OVERVIEW,
  URL_PIA,
  URL_POLICIES,
  URL_REPORT_SETTING,
  URL_REPORTS,
  URL_RISK_SCORE,
  URL_RULE_SET,
  URL_SETTINGS,
  URL_SMTP_CONFIG,
  URL_TEMPLATES,
  URL_TICKETS,
  URL_USERS_MANAGEMENT,
  URL_UNAUTHORIZED
} from '../../constants'
import { getUpdatedUrl } from '../../utils/urlUtil'

export interface UserPermission {
  module: string
  accessLevel: string
}

export class PermissionMapper {
  public userPermissions: BACKEND_PERMISSIONS[] = []
  public userScope: ROLE_SCOPES | undefined
  public userIsViewOnly: boolean | undefined

  constructor(
    permissions: IPermission[] = [],
    scope: ROLE_SCOPES | undefined,
    isViewOnly: boolean | undefined
  ) {
    this.userScope = scope
    this.userIsViewOnly = isViewOnly
    this.userPermissions = permissions
      .map((permission) => {
        const userPermissions: string[] = []

        if (permission.currentAccessLevel) {
          userPermissions.push(`${permission.permissionName}:${permission.currentAccessLevel}`)
        }

        if (permission.currentActions?.length) {
          permission.currentActions.forEach((action) => {
            userPermissions.push(`${permission.permissionName}:${action}`)
          })
        }

        return userPermissions as BACKEND_PERMISSIONS[]
      })
      .flat()

    // this.userScope = ROLE_SCOPES.global
    // this.userIsViewOnly = true
    // this.userPermissions = [...this.userPermissions, BACKEND_PERMISSIONS.ALERTS_LIMITED]
  }

  hasAction(action): boolean {
    const actionData = actionToBackendPermissionMap[action] || {}
    const isViewOnly = actionData.isViewOnly ?? false // if not specified, actions are not allowed for read-only users
    return this._isActionEnabled({ ...actionData, isViewOnly, action })
  }
  hasNavAction(action): boolean {
    const actionData = navActionToPermissionMap[action] || {}
    const isViewOnly = actionData.isViewOnly ?? true // if not specified, nav links are accessible for read-only users
    return this._isActionEnabled({ ...actionData, isViewOnly, action })
  }
  hasDataFilterAction(action): boolean {
    const actionData = dataFilterToPermissionMap[action] || {}
    const isViewOnly = actionData.isViewOnly ?? true // if not specified, read-only users have access to query/filter params
    return this._isDataFilterApplied({ ...actionData, isViewOnly, action })
  }

  hasAnyPermission(permissions: BACKEND_PERMISSIONS[]): boolean {
    return permissions.some((permission) => this.userPermissions.includes(permission))
  }

  getLandingUrl(baseUrl?: string): string {
    // 1. Datasource Owners always land on Data Sources
    if (this.userScope === ROLE_SCOPES.datasource) {
      return URL_DATA_SOURCES
    }

    // 2. Waterfall Permission Check
    // Check permissions in hierarchical order to find the first valid landing page.

    // --- Alerts & Policies ---
    if (
      this.hasAnyPermission([BACKEND_PERMISSIONS.ALERTS_FULL, BACKEND_PERMISSIONS.ALERTS_LIMITED])
    ) {
      return getUpdatedUrl({
        pathName: URL_POLICIES + URL_ALERTS,
        pageName: URL_ALERTS,
        pageQueryParams: { [IS_UNRESOLVED]: true },
        queryParams: { [FILTER_BY]: IS_ALL }
      })
    }

    if (
      this.hasAnyPermission([BACKEND_PERMISSIONS.POLICIES_FULL, BACKEND_PERMISSIONS.POLICIES_VIEW])
    ) {
      return URL_POLICIES + URL_RULE_SET
    }

    // --- Privacy Ops (PIA / ROPA) ---
    if (
      this.hasAnyPermission([
        BACKEND_PERMISSIONS.PRIVACY_OPS_ROPA_PIA_PROCESS_FULL,
        BACKEND_PERMISSIONS.PRIVACY_OPS_ROPA_PIA_PROCESS_LIMITED,
        BACKEND_PERMISSIONS.PRIVACY_OPS_FULL,
        BACKEND_PERMISSIONS.PRIVACY_OPS_VIEW
      ])
    ) {
      return (baseUrl || URL_PIA) + URL_DEFINED_PROCESSES
    }

    if (
      this.hasAnyPermission([
        BACKEND_PERMISSIONS.PRIVACY_OPS_ROPA_PIA_TEMPLATE_FULL,
        BACKEND_PERMISSIONS.PRIVACY_OPS_ROPA_PIA_TEMPLATE_LIMITED
      ])
    ) {
      return (baseUrl || URL_PIA) + URL_TEMPLATES
    }

    if (this.hasAnyPermission([BACKEND_PERMISSIONS.PRIVACY_OPS_ROPA_PIA_TICKET_LIMITED])) {
      return (baseUrl || URL_PIA) + URL_TICKETS
    }

    if (
      this.hasAnyPermission([
        BACKEND_PERMISSIONS.PRIVACY_OPS_ROPA_PIA_REPORT_FULL,
        BACKEND_PERMISSIONS.PRIVACY_OPS_ROPA_PIA_REPORT_LIMITED
      ])
    ) {
      return (baseUrl || URL_PIA) + URL_REPORTS
    }

    // --- Other Privacy Ops modules (DSR, Cookie Consent, Consent Management) ---
    if (this.hasAnyPermission([BACKEND_PERMISSIONS.PRIVACY_OPS_DSR_FULL])) {
      return URL_DSR + URL_DSR_REQUEST_MANAGEMENT
    }
    if (this.hasAnyPermission([BACKEND_PERMISSIONS.PRIVACY_OPS_CONSENT_MANAGEMENT_FULL])) {
      return URL_CONSENT_MANAGEMENT + URL_OVERVIEW
    }
    if (this.hasAnyPermission([BACKEND_PERMISSIONS.PRIVACY_OPS_COOKIE_CONSENT_FULL])) {
      return URL_COOKIES_CONSENT + URL_OVERVIEW
    }

    // --- Alerts read-only (observer access — lower priority than Privacy Ops) ---
    // Roles with only alerts:read-only land here after all PIA/privacy-ops checks have been exhausted.
    if (this.hasAnyPermission([BACKEND_PERMISSIONS.ALERTS_VIEW])) {
      return getUpdatedUrl({
        pathName: URL_POLICIES + URL_ALERTS,
        pageName: URL_ALERTS,
        pageQueryParams: { [IS_UNRESOLVED]: true },
        queryParams: { [FILTER_BY]: IS_ALL }
      })
    }

    // --- Insights / Global Settings (Granular Access) ---
    // Handles cases where a user might only have access to specific Insight modules
    // Order: Attributes → Labels → Templates (matching header dropdown order)

    if (
      this.hasAnyPermission([
        BACKEND_PERMISSIONS.ATTRIBUTES_FULL,
        BACKEND_PERMISSIONS.ATTRIBUTES_VIEW
      ])
    ) {
      return URL_INSIGHTS + URL_ATTRIBUTES
    }

    if (this.hasAnyPermission([BACKEND_PERMISSIONS.LABELS_FULL, BACKEND_PERMISSIONS.LABELS_VIEW])) {
      return URL_INSIGHTS + URL_LABEL_MANAGEMENT + URL_OVERVIEW
    }

    // Global Templates
    if (
      this.hasAnyPermission([
        BACKEND_PERMISSIONS.TEMPLATES_FULL,
        BACKEND_PERMISSIONS.TEMPLATES_VIEW
      ])
    ) {
      return URL_INSIGHTS + URL_TEMPLATES
    }

    if (
      this.hasAnyPermission([
        BACKEND_PERMISSIONS.USER_ENTITY_FULL,
        BACKEND_PERMISSIONS.USER_ENTITY_READ_ONLY
      ])
    ) {
      return URL_INSIGHTS + URL_ENTITIES
    }

    if (
      this.hasAnyPermission([
        BACKEND_PERMISSIONS.OBJECTS_FULL,
        BACKEND_PERMISSIONS.OBJECTS_READ_ONLY
      ])
    ) {
      return URL_INSIGHTS + URL_FILES
    }

    if (
      this.hasAnyPermission([
        BACKEND_PERMISSIONS.DOCUMENTS_FULL,
        BACKEND_PERMISSIONS.DOCUMENTS_READ_ONLY
      ])
    ) {
      return URL_INSIGHTS + URL_DOCUMENTS_CLASSIFIED
    }

    // --- Access Control / Settings ---
    if (this.hasNavAction(ACTIONS.GOVERNANCE_VIEW)) {
      return URL_ACCESS_CONTROL
    }

    if (
      this.hasNavAction(ACTIONS.USER_MANAGEMENT_VIEW) ||
      this.hasNavAction(ACTIONS.ROLE_MANAGEMENT_VIEW)
    ) {
      return URL_SETTINGS + URL_USERS_MANAGEMENT
    }

    if (this.hasNavAction(ACTIONS.AUTH_PROVIDES_VIEW)) {
      return URL_SETTINGS + URL_AUTH_PROVIDERS
    }

    if (this.hasNavAction(ACTIONS.SMTP_CONFIG_VIEW)) {
      return URL_SETTINGS + URL_SMTP_CONFIG
    }

    if (this.hasNavAction(ACTIONS.REPORT_CONFIG_VIEW)) {
      return URL_SETTINGS + URL_REPORT_SETTING
    }

    if (this.hasNavAction(ACTIONS.RISK_SCORE_CONFIG_VIEW)) {
      return URL_SETTINGS + URL_RISK_SCORE
    }

    // there is no specific view available if user has only these permissions:
    // object-viewer, alerts-audit-logs, sub-alert-management, ropa-pia-review

    // 3. Fallback
    return URL_UNAUTHORIZED
  }

  _isActionEnabled(actionData: ActionToPermissionType): boolean {
    const {
      scope: actionScope = ROLE_SCOPES.global,
      permissions: actionPermissions,
      mandatoryPermissions,
      isViewOnly: actionViewOnly
    } = actionData || {}

    const isScopeValid =
      this.userScope === ROLE_SCOPES.global
        ? true
        : Array.isArray(actionScope)
        ? actionScope.includes(this.userScope as ROLE_SCOPES)
        : actionScope === this.userScope

    const isNotReadOnlyValid = !!(this.userIsViewOnly ? actionViewOnly : true)

    const isPermissionsValid = actionPermissions?.length
      ? !!this.userPermissions.some((userPermission) => actionPermissions?.includes(userPermission))
      : true

    const isMandatoryPermissionsValid = mandatoryPermissions?.length
      ? !!this.userPermissions.some((userPermission) =>
          mandatoryPermissions?.includes(userPermission)
        )
      : true

    return isScopeValid && isNotReadOnlyValid && isPermissionsValid && isMandatoryPermissionsValid
  }

  _isDataFilterApplied(actionData: ActionToPermissionType): boolean {
    const {
      scope: filterScope = ROLE_SCOPES.global,
      permissions: actionPermissions,
      isViewOnly: actionViewOnly,
      action
    } = actionData || {}

    if (!filterScope) {
      console.error(`No scope provided for an action `, action)
      return false
    }

    const isScopeValid = Array.isArray(filterScope)
      ? filterScope.includes(this.userScope as ROLE_SCOPES)
      : filterScope === this.userScope

    const isReadOnlyValid = !!(this.userIsViewOnly ? actionViewOnly : true)

    const isPermissionsValid = actionPermissions?.length
      ? !!this.userPermissions.some((userPermission) => actionPermissions?.includes(userPermission))
      : true

    return isScopeValid && isReadOnlyValid && isPermissionsValid
  }
}
